import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';

import { getConfig } from '../../_metronic/_helpers';
import { getDocumentMetaWithDocId } from '../pages/api/api';

const DocumentSearch = ({ model, setModel }) => {
    const history = useHistory();
    const configData = getConfig();
    const menuItems = configData?.[0]?.menuItems || [];
    const [validateError, setValidateError] = useState(false);

    const [isLoading, setLoading] = useState(false);
    const [documentHandle, setDocumentHandle] = useState('');

    const onEnterKey = useCallback(
        (e) => {
            if (
                (e.code === 'Enter' || e.code === 'NumpadEnter') &&
                documentHandle.trim().length
            ) {
                handleRetrieve();
            }
        },
        [documentHandle]
    );

    useEffect(() => {
        document.addEventListener('keyup', onEnterKey);
        return () => document.removeEventListener('keyup', onEnterKey);
    }, [onEnterKey]);

    // Reset state when modal opens
    useEffect(() => {
        if (model) {
            setDocumentHandle('');
            setValidateError(false);
        }
    }, [model]);

    const handleClose = useCallback(() => {
        setModel(false);
        setDocumentHandle('');
        setValidateError(false);
    }, [setModel]);

    const findMenuItemByProcessAndStatus = (menuItems, processId, statusId) => {
        for (const menu of menuItems) {
            for (const process of menu.processes) {
                if (process.processId === processId) {
                    const status = process.statuses.find(
                        (status) => status.statusId === statusId
                    );
                    if (status) {
                        return {
                            menuId: menu.menuId,
                            menuName: menu.menuName,
                            processId: process.processId,
                            processName: process.processName,
                            statusId: status.statusId,
                            statusName: status.name,
                            statusIcon: status.icon,
                            statusColor: status.color,
                        };
                    }
                }
            }
        }
        return null;
    };

    const handleRetrieve = async () => {
        setLoading(true);
        try {
            const docHandle = await getDocumentMetaWithDocId({
                docid: documentHandle,
            });

            if (!!docHandle?.message) {
                setValidateError(true);
                return;
            }

            if (docHandle?.statuses?.length > 0) {
                const { processId, statusId } = docHandle.statuses[0];
                const menuItem = findMenuItemByProcessAndStatus(
                    menuItems,
                    processId,
                    statusId
                );

                if (menuItem) {
                    const menuName = menuItem.menuName.toLowerCase();
                    const statusName = menuItem.statusName
                        .toLowerCase()
                        .trim()
                        .replaceAll(' ', '-');

                    const path = `/${menuName}/${statusName}/${processId}/${statusId}/${documentHandle}`;
                    history.push(path);
                }
            } else {
                const path = `/document-handle/${documentHandle}`;
                history.push(path);
            }

            handleClose();
        } catch (error) {
            console.error('Error retrieving:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                className='document-search-modal'
                show={model}
                onHide={handleClose}
                animation={false}
                size='lg'
                centered
            >
                <Modal.Header
                    closeButton
                    className='d-block justify-content-start'
                >
                    <Modal.Title className='d-flex align-items-stretch justify-content-between'>
                        <div>
                            <i title='add' className='fa-regular fa-search' />
                            Retrieve by Document Handle
                        </div>
                        <i
                            title='Close'
                            className='fa-light fa-xmark d-flex justify-content-end align-items-center'
                            aria-hidden='true'
                            onClick={handleClose}
                        />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='text-dark mb-5'>
                        To search a document, enter the document handle.
                    </p>
                    <div className='form-group row'>
                        <div className='col-lg-8 transition-select'>
                            <input
                                className={`form-control ${
                                    validateError ? 'error-field' : ''
                                }`}
                                type='text'
                                placeholder='Enter Document Handle'
                                onChange={(e) =>
                                    setDocumentHandle(e.target.value)
                                }
                                autoFocus
                            />
                            {validateError && (
                                <p className='m-0 text-danger'>
                                    Document not found.
                                </p>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className='d-block'>
                    <div className='d-flex footer-btn align-items-stretch justify-content-between'>
                        <Button
                            title='Cancel'
                            variant='secondary'
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            title='Retrieve'
                            disabled={isLoading || !documentHandle?.length}
                            onClick={handleRetrieve}
                            variant='success'
                        >
                            {isLoading ? (
                                <span
                                    className='spinner-border spinner-border-sm'
                                    role='status'
                                    aria-hidden='true'
                                />
                            ) : (
                                'Retrieve'
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};
export default DocumentSearch;
